/**
 * useDraft Hook
 *
 * Manages draft CRUD operations for the Home Layout Manager.
 * Handles fetching, updating, and resetting the global draft.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNotify } from 'react-admin';
import { httpClient } from '../../../components/fetchApiData';
import config from '../../../config';
import Logger from '../../../utils/Logger';
import type {
  DraftState,
  GetDraftResponse,
  BannerConfig,
  CategoryConfig,
  BrandPositionsConfig,
  UserInfo,
} from '../types';

const API_BASE = `${config.API_URL}/admin/home-layout`;

interface UseDraftOptions {
  onDraftLoaded?: (draft: DraftState) => void;
  onDraftUpdated?: (draft: DraftState) => void;
  onError?: (error: Error) => void;
}

interface UseDraftReturn {
  draft: DraftState | null;
  liveState: DraftState | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastModifiedBy: UserInfo | null;
  lastModifiedAt: string | null;
  hasChanges: boolean;
  fetchDraft: () => Promise<void>;
  fetchLiveState: () => Promise<void>;
  updateDraft: (updates: Partial<DraftState>) => Promise<void>;
  updateBannerConfig: (config: Partial<BannerConfig>) => Promise<void>;
  updateCategoryConfig: (config: CategoryConfig) => Promise<void>;
  updateBrandPositions: (config: BrandPositionsConfig) => Promise<void>;
  resetDraft: () => Promise<void>;
  setDraft: React.Dispatch<React.SetStateAction<DraftState | null>>;
}

export function useDraft(options: UseDraftOptions = {}): UseDraftReturn {
  const { onDraftLoaded, onDraftUpdated, onError } = options;
  const notify = useNotify();

  // Use refs for callbacks to avoid useCallback dependency issues
  const onDraftLoadedRef = useRef(onDraftLoaded);
  const onDraftUpdatedRef = useRef(onDraftUpdated);
  const onErrorRef = useRef(onError);

  // Keep refs up to date
  useEffect(() => {
    onDraftLoadedRef.current = onDraftLoaded;
    onDraftUpdatedRef.current = onDraftUpdated;
    onErrorRef.current = onError;
  }, [onDraftLoaded, onDraftUpdated, onError]);

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [liveState, setLiveState] = useState<DraftState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastModifiedBy, setLastModifiedBy] = useState<UserInfo | null>(null);
  const [lastModifiedAt, setLastModifiedAt] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  /**
   * Fetch the current draft (or create one from live state)
   */
  const fetchDraft = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { json } = await httpClient(`${API_BASE}/draft`, { method: 'GET' });
      const response = json as GetDraftResponse;

      setDraft(response.draft);
      setLastModifiedBy(response.lastModifiedBy);
      setLastModifiedAt(response.lastModifiedAt);
      setHasChanges(response.hasChanges);

      onDraftLoadedRef.current?.(response.draft);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load draft';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
      onErrorRef.current?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  }, [notify]); // Stable dependency - no callbacks

  /**
   * Fetch the current live state for comparison
   * Note: API returns { bannerConfig, categoryConfig } directly, not wrapped in 'draft'
   */
  const fetchLiveState = useCallback(async () => {
    try {
      const { json } = await httpClient(`${API_BASE}/live`, { method: 'GET' });
      // API returns bannerConfig and categoryConfig directly (not wrapped in 'draft')
      const liveData = json as DraftState;
      setLiveState(liveData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load live state';
      Logger.error('Failed to fetch live state:', errorMessage);
    }
  }, []);

  /**
   * Update draft with partial changes
   */
  const updateDraft = useCallback(async (updates: Partial<DraftState>) => {
    if (!draft) return;

    setIsSaving(true);
    setError(null);

    // Optimistic update
    const previousDraft = draft;
    const newDraft = {
      ...draft,
      ...updates,
      bannerConfig: updates.bannerConfig
        ? { ...draft.bannerConfig, ...updates.bannerConfig }
        : draft.bannerConfig,
      categoryConfig: updates.categoryConfig
        ? { ...draft.categoryConfig, ...updates.categoryConfig }
        : draft.categoryConfig,
    };
    setDraft(newDraft);

    try {
      const { json } = await httpClient(`${API_BASE}/draft`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      const response = json as GetDraftResponse;

      setDraft(response.draft);
      setLastModifiedBy(response.lastModifiedBy);
      setLastModifiedAt(response.lastModifiedAt);
      setHasChanges(response.hasChanges);

      onDraftUpdatedRef.current?.(response.draft);
    } catch (err) {
      // Rollback on error
      setDraft(previousDraft);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
      onErrorRef.current?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsSaving(false);
    }
  }, [draft, notify]); // Stable dependencies - no callbacks

  /**
   * Update banner configuration
   */
  const updateBannerConfig = useCallback(async (config: Partial<BannerConfig>) => {
    if (!draft) return;

    await updateDraft({
      bannerConfig: {
        ...draft.bannerConfig,
        ...config,
      },
    });
  }, [draft, updateDraft]);

  /**
   * Update category configuration
   */
  const updateCategoryConfig = useCallback(async (config: CategoryConfig) => {
    await updateDraft({
      categoryConfig: config,
    });
  }, [updateDraft]);

  /**
   * Update brand positions configuration
   */
  const updateBrandPositions = useCallback(async (config: BrandPositionsConfig) => {
    await updateDraft({
      brandPositions: config,
    });
  }, [updateDraft]);

  /**
   * Reset draft to match current live state
   */
  const resetDraft = useCallback(async () => {
    setIsSaving(true);
    setError(null);

    try {
      const { json } = await httpClient(`${API_BASE}/draft/reset`, {
        method: 'POST',
      });
      const response = json as GetDraftResponse;

      setDraft(response.draft);
      setLastModifiedBy(response.lastModifiedBy);
      setLastModifiedAt(response.lastModifiedAt);
      setHasChanges(false);

      notify('Draft reset to live state', { type: 'success' });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset draft';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
      onErrorRef.current?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsSaving(false);
    }
  }, [notify]); // Stable dependencies - no callbacks

  return {
    draft,
    liveState,
    isLoading,
    isSaving,
    error,
    lastModifiedBy,
    lastModifiedAt,
    hasChanges,
    fetchDraft,
    fetchLiveState,
    updateDraft,
    updateBannerConfig,
    updateCategoryConfig,
    updateBrandPositions,
    resetDraft,
    setDraft,
  };
}

export default useDraft;
